// /src/components/core/InstallAppPrompt.jsx
// Mobile "Install" button. Install availability comes from the shared useInstallPrompt
// hook (single source of truth — no duplicated beforeinstallprompt/appinstalled wiring).
// - Android/desktop Chromium: canInstall true -> fires the native install prompt.
// - iOS: never fires the prompt, so this surface shows manual Add-to-Home-Screen steps.
// - Already installed (standalone) OR no prompt available + not iOS: renders nothing
//   (no dead button that dead-ends in an instructions modal).

import React, { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import useInstallPrompt from '../../hooks/useInstallPrompt';

function InstallAppPrompt() {
  const { t } = useTranslation('pwa');
  const { canInstall, install, isIOS, isStandalone } = useInstallPrompt();
  const [showIOSModal, setShowIOSModal] = useState(false);

  // Already installed → nothing to offer.
  if (isStandalone) return null;

  // Show the button only when a real prompt is held, or on iOS (manual instructions).
  // Everything else renders nothing — that kills the old unconditional dead button.
  if (!canInstall && !isIOS) return null;

  const handleInstallClick = () => {
    if (canInstall) {
      install();
    } else {
      // iOS: the native prompt never fires — show Add-to-Home-Screen instructions.
      setShowIOSModal(true);
    }
  };

  return (
    <>
      {/* Install button - yellow livery */}
      <button
        onClick={handleInstallClick}
        style={{
          backgroundColor: '#F9A61B',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          padding: '4px 10px',
          fontSize: '12px',
          fontWeight: 'bold',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          whiteSpace: 'nowrap'
        }}
      >
        <span style={{ fontSize: '14px' }}>📲</span>
        {t('install_button')}
      </button>

      {/* iOS Add-to-Home-Screen instructions — only reachable on iOS (canInstall false there) */}
      {showIOSModal && (
        <div
          onClick={() => setShowIOSModal(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '320px',
              width: '100%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
            }}
          >
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#1A1A1A',
              textAlign: 'center'
            }}>
              {t('modal_title')}
            </h3>

            <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
              <p style={{ margin: '0 0 12px 0' }}>
                {t('ios_intro')}
              </p>
              <ol style={{ margin: '0 0 16px 0', paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>
                  {t('ios_step1_prefix')} <strong>{t('ios_step1_share')}</strong> {t('ios_step1_suffix')}
                  <span style={{
                    display: 'inline-block',
                    marginLeft: '4px',
                    fontSize: '16px'
                  }}>⬆️</span>
                </li>
                <li style={{ marginBottom: '8px' }}>
                  {t('ios_step2_prefix')} <strong>{t('ios_step2_action')}</strong>
                </li>
                <li>
                  {t('ios_step3_prefix')} <strong>{t('ios_step3_action')}</strong> {t('ios_step3_suffix')}
                </li>
              </ol>
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '16px'
            }}>
              <button
                onClick={() => setShowIOSModal(false)}
                style={{
                  padding: '10px 32px',
                  fontSize: '14px',
                  border: 'none',
                  borderRadius: '8px',
                  backgroundColor: '#F9A61B',
                  color: 'white',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                {t('got_it_button')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InstallAppPrompt;
